
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    ArrowLeft,
    FileText,
    ClipboardList,
    Search
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function InternalBagPage() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [fromDate, setFromDate] = useState(format(new Date(), 'yyyy-MM-01'));
    const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [searchTerm, setSearchTerm] = useState('');

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/cartera/internal-bag-details', {
                params: { from: fromDate, to: toDate },
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching internal bag details:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fromDate, toDate]);

    const filteredData = data.filter(item =>
        item.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.parent_order_number?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalAmount = filteredData.reduce((sum, item) => sum + Number(item.internal_amount || 0), 0);

    return (
        <div className="p-6 bg-slate-50 min-h-screen font-outfit">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-4">
                    <Link to="/cartera/cashier" className="text-slate-500 hover:text-slate-700 transition-colors">
                        <ArrowLeft className="w-6 h-6" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">Bolsa Interna (INT)</h1>
                        <p className="text-slate-500">Detalle de dinero en efectivo no reportado a Siigo</p>
                    </div>
                </div>
                <div className="bg-emerald-100 p-4 rounded-xl border border-emerald-200">
                    <p className="text-sm text-emerald-600 font-medium">Total Acumulado</p>
                    <p className="text-2xl font-bold text-emerald-800">
                        $ {totalAmount.toLocaleString('es-CO')}
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 flex flex-wrap gap-4 items-center">
                <div className="flex items-center space-x-2">
                    <label className="text-sm text-slate-600">Desde:</label>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="px-3 py-2 border rounded-lg text-sm"
                    />
                </div>
                <div className="flex items-center space-x-2">
                    <label className="text-sm text-slate-600">Hasta:</label>
                    <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="px-3 py-2 border rounded-lg text-sm"
                    />
                </div>
                <div className="flex-1 max-w-md relative">
                    <Search className="w-5 h-5 absolute left-3 top-2.5 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Buscar pedido, cliente..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                    />
                </div>
                <button
                    onClick={fetchData}
                    className="ml-auto px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                >
                    Actualizar
                </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-slate-500">Cargando datos...</div>
                ) : filteredData.length === 0 ? (
                    <div className="p-12 text-center text-slate-500">No se encontraron registros en este periodo.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wider">
                                    <th className="px-6 py-4 font-semibold">Fecha</th>
                                    <th className="px-6 py-4 font-semibold">Pedido</th>
                                    <th className="px-6 py-4 font-semibold">Cliente</th>
                                    <th className="px-6 py-4 font-semibold">Método Entrega</th>
                                    <th className="px-6 py-4 font-semibold">Vinculado a (Siigo)</th>
                                    <th className="px-6 py-4 font-semibold text-right">Recaudado</th>
                                    <th className="px-6 py-4 font-semibold text-right">Rep. Siigo</th>
                                    <th className="px-6 py-4 font-semibold text-right">Valor Interno</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-sm">
                                {filteredData.map((item) => (
                                    <tr key={item.cash_register_id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-slate-600">
                                            {item.collected_at ? format(new Date(item.collected_at), 'dd MMM yyyy HH:mm', { locale: es }) : '-'}
                                        </td>
                                        <td className="px-6 py-4 font-medium text-slate-800">
                                            <span className="bg-slate-100 px-2 py-1 rounded text-slate-700 border border-slate-200">
                                                {item.order_number}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-slate-600 max-w-xs truncate" title={item.customer_name}>
                                            {item.customer_name}
                                        </td>
                                        <td className="px-6 py-4 text-slate-600 capitalize">
                                            {item.delivery_method?.replace(/_/g, ' ')}
                                        </td>
                                        <td className="px-6 py-4 text-slate-600">
                                            {item.parent_order_number ? (
                                                <span className="text-emerald-600 font-medium flex items-center gap-1">
                                                    <ClipboardList className="w-4 h-4" />
                                                    {item.parent_order_number}
                                                </span>
                                            ) : (
                                                <span className="text-slate-400 italic">No vinculado</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right text-slate-500">
                                            $ {Number(item.total_collected).toLocaleString('es-CO')}
                                        </td>
                                        <td className="px-6 py-4 text-right text-slate-500">
                                            $ {Number(item.siigo_reported_amount).toLocaleString('es-CO')}
                                        </td>
                                        <td className="px-6 py-4 text-right font-bold text-emerald-600">
                                            $ {Number(item.internal_amount).toLocaleString('es-CO')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
